/**
 * ComposerChip — the one chip shape both composer rows use.
 *
 * The context row and the attachment row sit directly on top of each other, so
 * any difference in height, radius or padding between them reads as a bug
 * rather than a distinction. They ARE different things — entities that persist
 * vs files staged for this turn — but that difference belongs in the well
 * (entity icon vs thumbnail/file-type) and the tone, not in the geometry.
 *
 * Deliberately not a generic app-wide chip: it encodes the composer's 18px
 * well + 150px label cap + 24px remove target, which is a composer decision.
 */

import { Box, HStack, IconButton, Text } from '@chakra-ui/react';
import type { ReactNode, Ref } from 'react';

import { MaterialIcon } from '../../ui/MaterialIcon';
import { Tooltip } from '../../ui/Tooltip';

export type ComposerChipTone = 'default' | 'active' | 'error';

interface ComposerChipProps {
  /** Contents of the 18px leading well — thumbnail, entity icon, spinner, letters. */
  readonly well: ReactNode;
  readonly label: string;
  /** Omit to withhold the remove affordance (e.g. while an upload is in flight). */
  readonly onRemove?: () => void;
  /** Must name the thing being removed — a scrolling row of chips otherwise
   *  exposes several buttons all called "Remove". */
  readonly removeLabel?: string;
  readonly onClick?: () => void;
  /** Required alongside `onClick` — names the primary action, not the chip. */
  readonly clickLabel?: string;
  readonly tone?: ComposerChipTone;
  /** Names the chip as a whole (a `group`) when its parts need one sentence
   *  a screen reader can announce, e.g. a spreadsheet's sheet and range. */
  readonly groupLabel?: string;
  /** Extra controls between the primary action and remove, as siblings of
   *  both (never nested inside the primary button). */
  readonly children?: ReactNode;
  /** Narrower label cap for a chip that also carries `children`, so the
   *  whole chip still fits the 352px chat panel. */
  readonly labelMaxW?: string;
  /** Tooltip on the primary action ONLY, never the whole chip, so hovering
   *  or tapping a remove control does not announce "Click to open". */
  readonly clickTooltip?: string;
  /** The chip itself (its `group`), made programmatically focusable
   *  (`tabIndex={-1}`, never in the tab order) so focus has somewhere to go
   *  when a control inside it unmounts. */
  readonly groupRef?: Ref<HTMLDivElement>;
}

/**
 * Border + text per tone. `active` is the in-flight state (purple hairline, no
 * separate progress bar — the chip IS the progress indicator); `error` is a
 * failed upload the user can click to retry.
 */
const TONE_STYLES: Record<ComposerChipTone, { borderColor: string; color: string }> = {
  default: { borderColor: 'border.default', color: 'text.primary' },
  active: { borderColor: 'brand.primary', color: 'text.primary' },
  error: { borderColor: 'border.error', color: 'text.error' },
};

export function ComposerChip({
  well,
  label,
  onRemove,
  removeLabel,
  onClick,
  clickLabel,
  tone = 'default',
  groupLabel,
  children,
  labelMaxW = '150px',
  clickTooltip,
  groupRef,
}: ComposerChipProps) {
  const { borderColor, color } = TONE_STYLES[tone];

  const body = (
    <>
      {/* 18px TALL and at least 18px wide, but free to grow: an image crop is
          a square, while a file-type badge ("PDF", "DOCX") needs a pill or the
          letters are squeezed to the point of being unreadable at this size. */}
      <Box
        flexShrink={0}
        minW="18px"
        h="18px"
        borderRadius="sm"
        bg="bg.subtle"
        display="flex"
        alignItems="center"
        justifyContent="center"
        overflow="hidden"
      >
        {well}
      </Box>

      {/* 150px cap keeps a scrolling row readable — past that one long title
          crowds every other chip out of view. */}
      <Text fontSize="xs" fontWeight="medium" color={color} noOfLines={1} maxW={labelMaxW}>
        {label}
      </Text>
    </>
  );

  return (
    <HStack
      ref={groupRef}
      tabIndex={groupRef ? -1 : undefined}
      role={groupLabel ? 'group' : undefined}
      aria-label={groupLabel}
      spacing={1}
      // px only — the 24px remove button already sets the height, so vertical
      // padding here would push the row past its budget for no visual gain.
      px={1.5}
      h="28px"
      flexShrink={0}
      bg="bg.muted"
      border="1px solid"
      borderColor={borderColor}
      borderRadius="md"
      _hover={onClick ? { borderColor: 'border.hover', bg: 'bg.hover' } : undefined}
      // The global `*:focus-visible` rule (theme/styles.ts) already rings a
      // focused chip, but OUTSET: ComposerScrollRow's scroller clips it to
      // 1px top and bottom and nothing at the sides. This is the same ring
      // (width and `border.focus` color) drawn INSET, so all four edges show.
      _focusVisible={
        groupRef
          ? {
              boxShadow:
                'inset 0 0 0 var(--chakra-borderWidths-interactive) var(--chakra-colors-border-focus)',
              outline: 'none',
            }
          : undefined
      }
      transition="all 0.2s"
      maxW="fit-content"
    >
      {/* The primary action and remove are SIBLING buttons, never nested — a
          <button> inside a <button> is invalid HTML, and browsers resolve it by
          dropping one of them, which is how a remove target silently stops
          working. */}
      {onClick ? (
        <Tooltip label={clickTooltip} isDisabled={!clickTooltip} placement="top" hasArrow>
          <HStack
            as="button"
            type="button"
            aria-label={clickLabel}
            onClick={onClick}
            spacing={1}
            minW={0}
            cursor="pointer"
            _focusVisible={{ boxShadow: 'outline', outline: 'none' }}
          >
            {body}
          </HStack>
        </Tooltip>
      ) : (
        body
      )}

      {children}

      {onRemove && (
        <IconButton
          aria-label={removeLabel ?? `Remove ${label}`}
          icon={<MaterialIcon name="close" boxSize={3.5} />}
          size="xs"
          variant="ghost"
          onClick={onRemove}
          // Keep the theme's xs size (24x24). Overriding to minW/h auto makes
          // this 22x22, under the WCAG 2.5.8 minimum — and this button nests
          // inside the chip's own click target, so the spacing exception can't
          // rescue it.
          borderRadius="sm"
          flexShrink={0}
          _hover={{ bg: 'bg.hover' }}
        />
      )}
    </HStack>
  );
}
